/**
 * Platform-aware file download/share utility.
 *
 * Priority on mobile:
 *   1. Native app bridge (ReactNativeWebView SHARE_FILE)
 *   2. Web Share API (navigator.share with File)
 *   3. Open in new tab (fallback for older mobile browsers)
 *
 * Desktop: standard blob download.
 */

const isMobileBrowser = () => {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

const isNativeApp = () =>
  !!window.FieldzNative?.isNativeApp || !!window.ReactNativeWebView;

/**
 * Download or share ANY file (PDF, audio, image, etc.).
 * @param {string} url        - full URL to fetch the file from (authenticated)
 * @param {string} filename   - desired filename e.g. "recording_abc.mp3"
 * @param {string} mimeType   - MIME type, e.g. "audio/mpeg", "application/pdf"
 * @param {string} [authToken] - Bearer token for the fetch
 * @returns {Promise<{method: string}>} which method was used
 */
export async function downloadOrShareFile(url, filename, mimeType, authToken) {
  // ── Native app: post message to bridge ──────────────────
  if (isNativeApp() && window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "SHARE_FILE",
        payload: { url, filename, mimeType, authToken },
      })
    );
    return { method: "native" };
  }

  // Fetch the file as blob
  const res = await fetch(url, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });
  if (!res.ok) throw new Error("File fetch failed");
  const blob = await res.blob();
  const file = new File([blob], filename, { type: mimeType });

  // ── Mobile browser: Web Share API ───────────────────────
  if (isMobileBrowser() && navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename });
      return { method: "webshare" };
    } catch (err) {
      // User cancelled share → not an error
      if (err.name === "AbortError") return { method: "webshare_cancelled" };
      // Share failed → fall through to fallback
    }
  }

  // ── Mobile browser fallback: open in new tab ────────────
  if (isMobileBrowser()) {
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    return { method: "mobile_newtab" };
  }

  // ── Desktop: standard download ──────────────────────────
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
  return { method: "desktop_download" };
}

/**
 * Back-compat wrapper — existing PDF call-sites keep working unchanged.
 */
export async function downloadOrSharePdf(url, filename, authToken) {
  return downloadOrShareFile(url, filename, "application/pdf", authToken);
}
